<script setup lang="ts">
import type { RadioModelValue } from "../Radio/VRadio.types";
import type { VRadioTileProps } from "./VRadioTile.types";
import { computed, inject } from "vue";
import { choiceTileBodyCva, choiceTileCva, choiceTileSceneCva, choiceTileStackedCva, choiceTileSubtitleCva, choiceTileTitleCva } from "../ChoiceTile/choiceTile.cva";
import VRadio from "../Radio/VRadio.vue";
import { RADIO_TILE_GROUP_KEY } from "./VRadioTile.types";

defineOptions({
  name: "VRadioTile",
});

const {
  value,
  title,
  subtitle = "",
  disabled = false,
  size = "md",
  testId = undefined,
} = defineProps<VRadioTileProps>();

defineSlots<{
  /**
   * The scene: 80x56 on the left of a `row` tile, centred in a 64px box over the full width of a `stacked` tile (an
   * illustration from the App Store illustration set). It is hidden from screen readers; the title names the option.
   */
  illustration?: () => unknown
  /** Extra content under the subtitle, e.g. a count or a chip. */
  default?: () => unknown
}>();

const group = inject(RADIO_TILE_GROUP_KEY, null);

if (!group) {
  throw new Error("VRadioTile must be used inside a VRadioTileGroup");
}

const isDisabled = computed(() => disabled || group.disabled.value);
const isSelected = computed(() => group.modelValue.value === value);

// The stacked tile has one board padding and radius; `size` only sets its text size there.
const stacked = computed(() => group.variant.value === "stacked");
const tileClass = computed(() => stacked.value
  ? choiceTileStackedCva({ selected: isSelected.value, disabled: isDisabled.value })
  : choiceTileCva({ selected: isSelected.value, disabled: isDisabled.value, size }));
const titleClass = computed(() =>
  choiceTileTitleCva({ disabled: isDisabled.value, size, variant: group.variant.value }));
const subtitleClass = computed(() => choiceTileSubtitleCva({ size, variant: group.variant.value }));
const bodyClass = computed(() => choiceTileBodyCva({ size }));
const sceneClass = computed(() => choiceTileSceneCva({ selected: isSelected.value }));

function onSelect(next: RadioModelValue) {
  if (isDisabled.value) {
    return;
  }
  group?.select(next);
}
</script>

<template>
  <label
    v-if="stacked"
    :class="tileClass"
    :aria-disabled="isDisabled || undefined"
    :data-state="isSelected ? 'checked' : 'unchecked'"
    :data-testid="testId"
    data-variant="stacked"
  >
    <span v-if="$slots.illustration" :class="sceneClass" aria-hidden="true" data-testid="radio-tile-scene">
      <slot name="illustration" />
    </span>
    <!-- The radio sits left, its 16px circle centred on the first 20px line of the title. -->
    <span class="flex items-start gap-2.5">
      <VRadio
        class="mt-0.5 shrink-0"
        :model-value="group.modelValue.value"
        :value="value"
        :name="group.name.value"
        :disabled="isDisabled"
        @update:model-value="onSelect"
      />
      <span :class="bodyClass">
        <span :class="titleClass">{{ title }}</span>
        <span v-if="subtitle" :class="subtitleClass">{{ subtitle }}</span>
        <slot />
      </span>
    </span>
  </label>
  <label
    v-else
    :class="tileClass"
    :aria-disabled="isDisabled || undefined"
    :data-state="isSelected ? 'checked' : 'unchecked'"
    :data-testid="testId"
  >
    <span v-if="$slots.illustration" class="flex shrink-0" aria-hidden="true">
      <slot name="illustration" />
    </span>
    <span :class="bodyClass">
      <span :class="titleClass">{{ title }}</span>
      <span v-if="subtitle" :class="subtitleClass">{{ subtitle }}</span>
      <slot />
    </span>
    <VRadio
      class="shrink-0"
      :model-value="group.modelValue.value"
      :value="value"
      :name="group.name.value"
      :disabled="isDisabled"
      @update:model-value="onSelect"
    />
  </label>
</template>
